import { isPostalCodeRequired } from "../addressLocation/addressCountryRules";

const CARD_ERROR_FIELDS = {
  invalid_number: "number",
  incorrect_number: "number",
  incomplete_number: "number",
  expired_card: "expiry",
  invalid_expiry_month: "expiry",
  invalid_expiry_year: "expiry",
  incomplete_expiry: "expiry",
  invalid_cvc: "cvc",
  incorrect_cvc: "cvc",
  incomplete_cvc: "cvc",
};

const MESSAGE_KEYS = {
  invalid_number: "invalidCardNumber",
  incorrect_number: "invalidCardNumber",
  incomplete_number: "incompleteCardNumber",
  expired_card: "expiredCard",
  invalid_expiry_month: "invalidExpiry",
  invalid_expiry_year: "invalidExpiry",
  incomplete_expiry: "incompleteExpiry",
  invalid_cvc: "invalidCvc",
  incorrect_cvc: "incorrectCvc",
  incomplete_cvc: "incompleteCvc",
  card_declined: "cardDeclined",
  generic_decline: "cardDeclined",
  insufficient_funds: "insufficientFunds",
  lost_card: "cardDeclined",
  stolen_card: "cardDeclined",
  processing_error: "processingError",
  payment_intent_authentication_failure: "authenticationFailed",
  setup_intent_authentication_failure: "authenticationFailed",
};

const FALLBACKS = {
  invalidCardNumber: "Your card number is invalid.",
  incompleteCardNumber: "Please complete your card number.",
  expiredCard: "Your card has expired.",
  invalidExpiry: "Your card's expiration date is invalid.",
  incompleteExpiry: "Your card's expiration date is incomplete.",
  invalidCvc: "Your card's security code is invalid.",
  incorrectCvc: "Your card's security code is incorrect.",
  incompleteCvc: "Your card's security code is incomplete.",
  cardDeclined: "Your card was declined. Please try another payment method.",
  insufficientFunds: "Your card has insufficient funds.",
  processingError: "The card could not be processed. Please try again.",
  authenticationFailed: "Card authentication failed. Please try again.",
  networkError: "Unable to reach the payment service. Please check your connection and try again.",
  unexpectedError: "Unable to add this payment method. Please try again.",
};

export const getPaymentErrorDetails = (value) => {
  if (!value) return {};
  if (value.errorDetails) return { ...value.errorDetails, status: value.status };
  if (value.error && typeof value.error === "object") {
    return { ...value.error, status: value.status || value.error.status };
  }
  return {
    type: value.type,
    code: value.code,
    decline_code: value.decline_code,
    message: typeof value.message === "string"
      ? value.message
      : (typeof value.error === "string" ? value.error : ""),
    status: value.status,
  };
};

export const normalizePaymentMethodError = (value, translate = (_, fallback) => fallback) => {
  const error = getPaymentErrorDetails(value);
  const code = error.decline_code || error.code || "";
  const key = MESSAGE_KEYS[code];
  const isNetworkError = value?.name === "TypeError" || error.type === "network_error" || error.status === 0;

  let message;
  if (key) {
    message = translate(key, FALLBACKS[key]);
  } else if (isNetworkError) {
    message = translate("networkError", FALLBACKS.networkError);
  } else if (error.message && !/^\[object Object\]$/.test(error.message)) {
    message = error.message;
  } else {
    message = translate("unexpectedError", FALLBACKS.unexpectedError);
  }

  return {
    message,
    field: CARD_ERROR_FIELDS[code] || "general",
    type: error.type || "",
    code: error.code || "",
    declineCode: error.decline_code || "",
    status: error.status,
  };
};

export const isValidFullName = (name) => {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  return parts.length >= 2 && parts.every((part) => /^[^\d\s]{2,}$/.test(part));
};

export const validateBillingAddress = (address, translate = (_, fallback) => fallback) => {
  if (!address) return translate("billingRequired", "Billing address is required.");
  if (!String(address.country || "").trim() || address.country === "Selected") {
    return translate("countryRequired", "Country is required.");
  }
  // Only where the country uses postal codes (shared address rules).
  if (isPostalCodeRequired(address.country) && !String(address.postal_code || "").trim()) {
    return translate("postalCodeRequired", "Postal code is required.");
  }
  if (!String(address.line1 || "").trim() || !String(address.city || "").trim()) {
    return translate("billingIncomplete", "Billing address is incomplete.");
  }
  return "";
};
